import React, { ReactElement } from 'react';

import AntLayout from 'antd/es/layout';
import { Button, ConfigProvider, Space, theme } from 'antd';
import styles from './footer.module.css';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

export interface AudienceCreationFooterProps {
  children?: ReactElement;
  isDisabled?: boolean;
  isLoading?: boolean;
  handleNextStep?: () => void;
  handlePreviousStep?: () => void;
  handleDraftSave?: () => void;
  currentStep: number;
  formSteps: string[];
  className?: string;
}
const { Footer: AntFooter } = AntLayout;

export function AudienceCreationFooter({
  isDisabled,
  isLoading,
  children,
  handleNextStep,
  handlePreviousStep,
  handleDraftSave,
  currentStep,
  formSteps,
  className,
}: AudienceCreationFooterProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === formSteps.length - 1;
  return (
    <AntFooter
      className={cx(styles.footer, className)}
      style={{
        borderTop: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Space className={children && styles.space}>
        {children}
        <div className={styles.controls}>
          {isFirstStep ? (
            <ConfigProvider
              theme={{
                components: {
                  Button: {
                    defaultActiveBorderColor: token.colorError,
                    defaultActiveColor: token.colorError,
                    defaultColor: token.colorError,
                    defaultHoverBorderColor: token.colorError,
                    defaultHoverColor: token.colorError,
                  },
                },
                token: {
                  colorPrimaryHover: token.colorError,
                  colorTextBase: token.colorError,
                  colorBorder: token.colorError,
                  colorPrimaryActive: token.colorError,
                  colorPrimary: token.colorError,
                },
              }}
            >
              <NavLink to="..">
                <Button
                  data-testid="audienceCreationCancelButton"
                  size="middle"
                  type="text"
                  icon={<CloseOutlined rev={undefined} />}
                >
                  {t('cancel')}
                </Button>
              </NavLink>
            </ConfigProvider>
          ) : (
            handlePreviousStep && (
              <Button
                data-testid="audienceCreationPreviousButton"
                size="middle"
                type="default"
                disabled={isDisabled}
                loading={isLoading}
                onClick={handlePreviousStep}
                icon={<LeftOutlined rev={undefined} />}
              >
                {t('prev_step_button_text')}
              </Button>
            )
          )}

          {!isLastStep && (
            <Button
              data-testid="audienceCreationSaveDraftButton"
              type="default"
              size="middle"
              onClick={handleDraftSave}
              disabled={isDisabled}
              loading={isLoading}
            >
              {t('save_draft')}
            </Button>
          )}

          <Button
            data-testid="audienceCreationNextButton"
            // The label flips to "finish" on the last step, and a test must never read that from
            // rendered copy, so the state goes on the element instead.
            data-is-last-step={isLastStep}
            type="primary"
            disabled={isDisabled}
            loading={isLoading}
            htmlType={isLastStep ? 'submit' : 'button'}
            onClick={handleNextStep}
          >
            {isLastStep ? t('general_finish-btn') : t('next_step_button_text')}
            <RightOutlined rev={undefined} />
          </Button>
        </div>
      </Space>
    </AntFooter>
  );
}
